import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Wallet, ShieldCheck, ShieldAlert, ArrowDownRight, ArrowUpRight, Activity,
  ArrowDownToLine, ArrowUpFromLine, Copy, Bitcoin, TrendingUp, TrendingDown, X, MessageCircle,
} from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { PriceChart, Sparkline } from "@/components/CryptoChart";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api, { fmtCurrency, fmtDate, formatApiErrorDetail } from "@/lib/api";

const WHATSAPP_NUMBER = "393512182005";
const WHATSAPP_TEXT = encodeURIComponent(
  "Hello, and thank you for contacting Crypto-LegalUnion.\n\n" +
  "We have received your message and appreciate you reaching out to us.\n\n" +
  "Our team handles enquiries relating to cryptocurrency cases, blockchain investigations, digital asset tracing, and recovery matters. Your enquiry is important to us, and a member of our team will review your message and get back to you as soon as possible.\n\n" +
  "To help us understand your case, please provide a brief description of what happened, including any relevant details you may have.\n\n" +
  "Crypto-LegalUnion\n" +
  "Blockchain Intelligence | Digital Asset Tracing | Legal Coordination\n" +
  "In collaboration with LegalUnion\n" +
  "50 Bank Street, Canary Wharf, London E14 5NT, United Kingdom\n\n" +
  "Thank you for contacting us. We look forward to assisting you."
);
const HERO_ACCENT = "https://images.unsplash.com/photo-1761437856376-2ce1c483343b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="client-modal">
      <div className="absolute inset-0 bg-sapphire/80 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ opacity: 0, y: 16, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        className="relative glass-strong rounded-2xl w-full max-w-lg p-6 shadow-[0_8px_40px_rgba(0,0,0,0.5)]">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-bold tracking-tight">{title}</h3>
          <button onClick={onClose} data-testid="client-modal-close" className="text-white/50 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </motion.div>
    </div>
  );
}

export default function ClientDashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [coins, setCoins] = useState(() => {
    try { return JSON.parse(localStorage.getItem("clu_coins_cache")) || []; } catch { return []; }
  });
  const [modal, setModal] = useState(null);

  const load = () => api.get("/client/dashboard").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => {
    load();
    const loadPrices = () => api.get("/crypto/prices").then(({ data }) => {
      if (data.coins?.length) {
        setCoins(data.coins);
        try { localStorage.setItem("clu_coins_cache", JSON.stringify(data.coins)); } catch { /* ignore */ }
      }
    }).catch(() => {});
    loadPrices();
    const t = setInterval(loadPrices, 60000);
    return () => clearInterval(t);
  }, []);

  const me = data?.user || user;
  const txns = data?.transactions || [];
  const btc = coins.find((c) => c.symbol === "BTC");
  const eth = coins.find((c) => c.symbol === "ETH");
  const btcEquiv = btc?.price ? (Number(me?.balance || 0) / btc.price) : null;
  const wallet = me?.deposit_wallet || "bc1q2n58zfsrf2zn84e6fljp5vd9nhprd6x9uw7n7z";

  const copyWallet = () => {
    navigator.clipboard?.writeText(wallet);
    toast.success(t("cd.copied"));
  };

  return (
    <div className="relative min-h-screen">
      <div className="clu-ambient" />
      <div className="relative z-10">
        <SiteHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="client-dashboard">
          {/* Header */}
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold mb-2">{t("cd.portal")}</p>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">
                  <span className="font-light text-white/60">{t("cd.welcome")}</span> {me?.first_name || "Client"}
                </h1>
                {me?.verified ? (
                  <span data-testid="verified-badge" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/40 text-emerald-400 text-xs font-mono uppercase tracking-widest">
                    <ShieldCheck className="w-3.5 h-3.5" /> {t("cd.verified")}
                  </span>
                ) : (
                  <span data-testid="unverified-badge" className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gold/50 text-gold text-xs font-mono uppercase tracking-widest">
                    <ShieldAlert className="w-3.5 h-3.5" /> {t("cd.unverified")}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-3">
              <button data-testid="open-deposit-btn" onClick={() => setModal("deposit")}
                className="px-4 py-2.5 rounded-md bg-gold hover:bg-gold-soft text-sapphire font-semibold flex items-center gap-2 transition-colors">
                <ArrowDownToLine className="w-4 h-4" /> {t("cd.deposit")}
              </button>
              <button data-testid="open-withdraw-btn" onClick={() => setModal("withdraw")}
                className="px-4 py-2.5 rounded-md border border-white/20 hover:border-gold/50 hover:bg-white/5 flex items-center gap-2 transition-colors">
                <ArrowUpFromLine className="w-4 h-4" /> {t("cd.withdraw")}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-5">
            {/* Balance hero */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-2 glass rounded-2xl p-8 relative overflow-hidden" data-testid="balance-card">
              <div className="absolute inset-0 bg-cover bg-center opacity-15" style={{ backgroundImage: `url(${HERO_ACCENT})` }} />
              <div className="absolute top-5 right-5"><Wallet className="w-6 h-6 text-gold" /></div>
              <div className="relative">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-white/50 mb-6">{t("cd.escrow")}</p>
                <div className="font-mono text-5xl font-bold" data-testid="balance-eur">
                  {fmtCurrency(me?.balance, me?.currency || "EUR")}
                </div>
                <div className="mt-2 font-mono text-lg text-white/60" data-testid="balance-btc">
                  {btcEquiv != null ? `≈ ${btcEquiv.toFixed(8)} BTC` : "≈ … BTC"}
                </div>
                <div className="mt-6 h-px bg-white/10" />
                <p className="text-xs text-white/40 mt-4 font-mono">{t("cd.balanceNote")}</p>
              </div>
            </motion.div>

            {/* BTC card */}
            <PriceMini coin={btc} label={t("cd.btcLive")} icon={Bitcoin} testid="btc-card" priceTestid="btc-price" delay={0.05} />
            {/* ETH card */}
            <PriceMini coin={eth} label={t("cd.ethLive")} icon={TrendingUp} testid="eth-card" delay={0.1} />

            {/* Main BTC chart */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
              className="lg:col-span-4 glass rounded-2xl p-6" data-testid="btc-chart-panel">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Bitcoin className="w-5 h-5 text-gold" />
                  <span className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">{t("cd.btc7")}</span>
                </div>
                {btc && (
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-lg font-bold">{fmtCurrency(btc.price)}</span>
                    <span className={`font-mono text-sm flex items-center gap-1 ${btc.change_24h >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {btc.change_24h >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                      {btc.change_24h >= 0 ? "+" : ""}{btc.change_24h.toFixed(2)}%
                    </span>
                  </div>
                )}
              </div>
              <PriceChart data={btc?.sparkline || []} color={btc && btc.change_24h < 0 ? "#EF4444" : "#D4AF37"} height={260} />
            </motion.div>

            {/* Deposit wallet */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="lg:col-span-2 glass rounded-2xl p-6" data-testid="deposit-wallet-panel">
              <div className="flex items-start justify-between mb-5 gap-4">
                <div>
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold mb-2">{t("cd.depositEyebrow")}</p>
                  <h2 className="font-display text-xl font-bold tracking-tight">{t("cd.fundCase")}</h2>
                </div>
                <div className="bg-white p-1.5 rounded-lg shrink-0">
                  <img alt="QR" className="w-20 h-20" data-testid="wallet-qr"
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${wallet}`} />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch gap-2">
                <div data-testid="deposit-wallet-address" className="flex-1 bg-sapphire/60 border border-white/15 rounded-md px-3 py-2.5 font-mono text-xs break-all">{wallet}</div>
                <button data-testid="copy-wallet-btn" onClick={copyWallet}
                  className="px-4 py-2.5 rounded-md border border-white/20 hover:border-gold/50 hover:bg-white/5 transition-colors flex items-center justify-center gap-2 text-sm">
                  <Copy className="w-4 h-4" /> Copy
                </button>
              </div>
              <p className="text-xs text-white/40 mt-3 font-mono">Only send Bitcoin (BTC). Other networks may result in loss of funds.</p>
            </motion.div>

            {/* Market snapshot */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.18 }}
              className="lg:col-span-2 glass rounded-2xl p-6" data-testid="market-snapshot">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-4 h-4 text-gold" />
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">{t("cd.snapshot")}</p>
              </div>
              <div className="space-y-1">
                {coins.length === 0 && <p className="text-sm text-white/40 py-6 text-center">Loading market…</p>}
                {coins.slice(0, 6).map((c) => (
                  <div key={c.id} className="flex items-center gap-3 py-2 border-b border-white/5 last:border-0">
                    <img src={c.image} alt={c.symbol} className="w-6 h-6" />
                    <span className="font-mono text-sm text-white/80 w-12">{c.symbol}</span>
                    <div className="flex-1 max-w-[90px]"><Sparkline data={c.sparkline} color={c.change_24h >= 0 ? "#10B981" : "#EF4444"} height={28} /></div>
                    <span className="font-mono text-sm ml-auto">{fmtCurrency(c.price)}</span>
                    <span className={`font-mono text-xs w-16 text-right ${c.change_24h >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                      {c.change_24h >= 0 ? "+" : ""}{c.change_24h.toFixed(2)}%
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </main>
      </div>

      {/* WhatsApp FAB */}
      <a data-testid="whatsapp-fab" href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_TEXT}`} target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1EBE5C] shadow-[0_8px_24px_rgba(37,211,102,0.4)] flex items-center justify-center transition-colors"
        title="Chat with support">
        <MessageCircle className="w-6 h-6 text-white" />
      </a>

      {modal === "deposit" && (
        <Modal title={t("cd.depositTitle")} onClose={() => setModal(null)}>
          <div className="flex justify-center mb-4">
            <div className="bg-white p-2 rounded-lg">
              <img alt="QR" className="w-40 h-40" src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${wallet}`} />
            </div>
          </div>
          <p className="text-xs font-mono uppercase tracking-widest text-white/50 mb-2 text-center">{t("cd.depositAddr")}</p>
          <div className="flex items-stretch gap-2">
            <div className="flex-1 bg-sapphire/60 border border-white/15 rounded-md px-3 py-2.5 font-mono text-xs break-all">{wallet}</div>
            <button onClick={copyWallet} className="px-4 rounded-md border border-white/20 hover:bg-white/5 flex items-center gap-2 text-sm"><Copy className="w-4 h-4" /></button>
          </div>
          <p className="text-xs text-white/40 mt-4 font-mono text-center">{t("cd.depositConfirm")}</p>
        </Modal>
      )}

      {modal === "withdraw" && <WithdrawModal me={me} t={t} onClose={() => setModal(null)} onDone={load} />}
    </div>
  );
}

function PriceMini({ coin, label, icon: Icon, testid, priceTestid, delay = 0 }) {
  const up = (coin?.change_24h ?? 0) >= 0;
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }}
      className="glass rounded-2xl p-6 flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-gold" />
        <span className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">{label}</span>
      </div>
      <div className="font-mono text-2xl font-bold" data-testid={priceTestid}>{coin ? fmtCurrency(coin.price) : "—"}</div>
      <div className={`flex items-center gap-1 mt-1 font-mono text-sm ${up ? "text-emerald-400" : "text-red-400"}`}>
        {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {coin ? `${up ? "+" : ""}${coin.change_24h.toFixed(2)}%` : "0.00%"}
      </div>
      <div className="mt-auto pt-3"><Sparkline data={coin?.sparkline || []} color={up ? "#10B981" : "#EF4444"} height={40} /></div>
    </motion.div>
  );
}

function WithdrawModal({ me, onClose, onDone }) {
  const { t } = useLang();
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);

  const taxEnabled = me?.withdraw_tax_enabled !== false;
  const taxPercent = (me?.verified || !taxEnabled) ? 0 : Number(me?.withdraw_tax_percent ?? 10);
  const rate = Number(me?.exchange_rate ?? 325);
  const amt = parseFloat(amount) || 0;
  const taxAmount = +(amt * taxPercent / 100).toFixed(2);
  const cur = me?.currency || "EUR";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/client/withdraw", { amount: amt, address });
      if (!me?.verified) {
        toast.warning(`A ${taxPercent}% verification tax applies to unverified withdrawals. Our team will contact you to complete verification.`);
      } else {
        toast.success("Withdrawal request submitted. Our team will process it shortly.");
      }
      onClose();
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={t("cd.wdTitle")} onClose={onClose}>
      {!me?.verified && (
        <>
          {!taxEnabled && (
            <div className="mb-3 flex items-center gap-3 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3" data-testid="wd-suspended-notice">
              <ShieldAlert className="w-5 h-5 text-red-400 shrink-0" />
              <span className="text-sm font-semibold text-red-400">{t("cd.suspended")}</span>
            </div>
          )}
          <div className="mb-3 flex items-center justify-between rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-3" data-testid="wd-exchange-rate">
            <span className="text-xs font-mono uppercase tracking-[0.15em] text-white/70">{t("cd.exchangeRate")}</span>
            <span className="font-mono text-lg font-bold text-emerald-400">{rate}€</span>
          </div>
          {taxEnabled && (
            <div className="mb-4 flex items-start gap-3 rounded-md border border-gold/30 bg-gold/10 p-3" data-testid="wd-tax-notice">
              <ShieldAlert className="w-5 h-5 text-gold shrink-0 mt-0.5" />
              <p className="text-xs text-white/70">
                {t("cd.wdNotice")}{" "}
                <span className="text-gold font-semibold" data-testid="wd-tax-percent">{taxPercent}%</span> {t("cd.wdNotice2")}
              </p>
            </div>
          )}
        </>
      )}
      <form className="space-y-3" onSubmit={submit}>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.15em] text-white/60 font-mono block mb-1.5">{t("cd.amount")} ({cur})</span>
          <input data-testid="wd-amount" required type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="clu-input" />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.15em] text-white/60 font-mono block mb-1.5">{t("cd.destination")}</span>
          <input data-testid="wd-address" required value={address} onChange={(e) => setAddress(e.target.value)} className="clu-input" />
        </label>
        {amt > 0 && (
          <div className="rounded-md border border-white/10 bg-white/5 p-3 space-y-1.5 font-mono text-sm" data-testid="wd-breakdown">
            <div className="flex justify-between text-white/60"><span>{t("cd.requested")}</span><span>{fmtCurrency(amt, cur)}</span></div>
            {taxPercent > 0 && (
              <div className="flex justify-between text-white/60"><span>{t("cd.verifTax")} ({taxPercent}%)</span><span className="text-red-400">{fmtCurrency(taxAmount, cur)}</span></div>
            )}
          </div>
        )}
        <button type="submit" data-testid="wd-submit-btn" disabled={busy} className="w-full bg-gold hover:bg-gold-soft disabled:opacity-50 text-sapphire font-semibold py-2.5 rounded-md transition-colors">
          {busy ? t("cd.submitting") : t("cd.submit")}
        </button>
      </form>
    </Modal>
  );
}
